import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Input,
  HStack,
  VStack,
  Text,
  Tag,
  TagLabel,
  TagCloseButton,
  Select,
  Box,
  Wrap,
  WrapItem,
  useBreakpointValue,
} from "@chakra-ui/react";
import { FaCopy } from "react-icons/fa";
import { toast } from "react-toastify";
import httpInjectorService from "services/http-injector.service";

const ShareItemPermision = ({ isOpen, onClose, space_id, item_id }) => {
  const [email, setEmail] = useState("");
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [access, setAccess] = useState("viewer");
  const [message, setMessage] = useState("");

  const inputSize = useBreakpointValue({ base: "sm", md: "md" });

  const shareLink = `${window.location.origin}/admin/confluence/space/${space_id}/page/${item_id}`;

  // Fetch users
  const getSpaceUsers = async () => {
    try {
      const res = await httpInjectorService.getSpaceUser(space_id);
      if (res?.status === "success") {
        setUsers(res.data);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to load users");
    }
  };

  useEffect(() => {
    if (isOpen && space_id) getSpaceUsers();
  }, [isOpen, space_id]);

  // Clear message if no users
  useEffect(() => {
    if (selectedUsers.length === 0) setMessage("");
  }, [selectedUsers]);

  const addUser = (user) => {
    if (!user) return;
    if (selectedUsers.some((u) => u.user_id === user.user_id)) {
      toast.error("User already added");
      return;
    }
    setSelectedUsers([...selectedUsers, user]);
    setEmail("");
  };

  const removeUser = (id) => {
    setSelectedUsers(selectedUsers.filter((u) => u.user_id !== id));
  };

  const copyLink = () => {
    navigator.clipboard.writeText(shareLink);
    toast.success("Link copied");
  };

  const handleShare = async () => {
    try {
      const payload = {
        space_id,
        item_id,
        shared_with: selectedUsers.map((u) => u.user_id),
        role: access,
        shareLink,
        message,
        avatarColor:"rgb(45, 186, 189)",
      };

      const res = await httpInjectorService.shareItem(payload);

      if (res?.status === "success") {
        toast.success("Shared successfully");
         setEmail("");
      setSelectedUsers([]);
      setAccess("viewer");
      setMessage("");
        onClose();
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong while sharing.");
    }
  };

  const filteredUsers = users.filter(
    (u) =>
      u.email.toLowerCase().includes(email.toLowerCase()) &&
      !selectedUsers.some((s) => s.user_id === u.user_id)
  );

  const emailExists = users.some(
    (u) => u.email.toLowerCase() === email.toLowerCase()
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={{ base: "full", md: "lg" }}
      isCentered
      motionPreset="scale"
    >
      <ModalOverlay />

      <ModalContent
        bg="white"
        color="black"
        borderRadius={{ base: "0", md: "xl" }}
        mx={{ base: 2, md: 0 }}
      >
        <ModalHeader fontSize={{ base: "lg", md: "xl" }} fontWeight="semibold">
          Share Item
        </ModalHeader>

        <ModalCloseButton />

        <ModalBody maxH="70vh" overflowY="auto">
          <VStack spacing={3} align="stretch">

            {/* Email Input */}
            <Input
              placeholder="Enter user email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              size={inputSize}
            />

            {/* Autocomplete */}
            {email && (
              <Box
                border="1px solid"
                borderColor="gray.200"
                borderRadius="md"
                maxH="200px"
                overflowY="auto"
              >
                {filteredUsers.length > 0 ? (
                  filteredUsers.map((user) => (
                    <HStack
                      key={user.user_id}
                      justifyContent="space-between"
                      px={3}
                      py={2}
                      _hover={{ bg: "gray.100", cursor: "pointer" }}
                      onClick={() => addUser(user)}
                      borderBottom="1px solid"
                      borderColor="gray.100"
                    >
                      <Text fontSize={{ base: "sm", md: "md" }}>
                        {user.username}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {user.email}
                      </Text>
                    </HStack>
                  ))
                ) : (
                  !emailExists && (
                    <Box px={3} py={2} color="red.500">
                      User not found. Add the user to the space first.
                    </Box>
                  )
                )}
              </Box>
            )}

            {/* Selected Users */}
            <Wrap spacing={2} maxW="100%">
              {selectedUsers.map((user) => (
                <WrapItem key={user.user_id}>
                  <Tag size="sm" borderRadius="full" colorScheme="blue">
                    <TagLabel>{user.username}</TagLabel>
                    <TagCloseButton
                      onClick={() => removeUser(user.user_id)}
                    />
                  </Tag>
                </WrapItem>
              ))}
            </Wrap>

            {/* Access */}
            <Box>
              <Text fontWeight="bold" mb={1}>
                Access level
              </Text>

              <Select
                value={access}
                onChange={(e) => setAccess(e.target.value)}
                size={inputSize}
              >
                <option value="viewer">View</option>
                <option value="editor">Edit</option>
              </Select>
            </Box>

            {/* Message */}
            {selectedUsers.length > 0 && (
              <Box>
                <Text fontWeight="bold" mb={1}>
                  Message
                </Text>

                <Input
                  placeholder="Enter message for user"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  size={inputSize}
                />
              </Box>
            )}

            {/* Share Link */}
            <HStack
              spacing={2}
              flexDirection={{ base: "column", sm: "row" }}
              align="stretch"
            >
              <Input value={shareLink} isReadOnly size={inputSize} flex="1" />

              <Button
                onClick={copyLink}
                size={inputSize}
                w={{ base: "100%", sm: "auto" }}
              >
                <FaCopy />
              </Button>
            </HStack>

          </VStack>
        </ModalBody>

        {/* Footer */}
        <ModalFooter flexDirection={{ base: "column", sm: "row" }} gap={2}>
          <Button
            variant="ghost"
            onClick={onClose}
            size={inputSize}
            w={{ base: "100%", sm: "auto" }}
          >
            Cancel
          </Button>

          <Button
            colorScheme="blue"
            onClick={handleShare}
            size={inputSize}
            isDisabled={selectedUsers.length === 0}
            w={{ base: "100%", sm: "auto" }}
          >
            Share
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ShareItemPermision;